import { styled } from '@mui/material'
import { Calendar } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { MUIThemeColors } from '../../../../mui-theme'
import { getThemeColor } from '../../../../utils'

export const EventDetails = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
  width: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',

  '.event-title': {
    fontWeight: 600,
    fontSize: '11px',
    lineHeight: 1.2,
    color: getThemeColor(theme, MUIThemeColors.primaryDark),
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    flexShrink: 1,
    minWidth: 0,
  },

  '.event-separator': {
    fontSize: '10px',
    color: getThemeColor(theme, MUIThemeColors.secondaryMain),
    opacity: 0.7,
    flexShrink: 0,
  },

  '.event-locality': {
    fontSize: '10px',
    lineHeight: 1.2,
    color: getThemeColor(theme, MUIThemeColors.secondaryMain),
    opacity: 0.85,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    flexShrink: 1,
    minWidth: 0,
  },
}))

export const StyledCalendar = styled(Calendar)(({ theme }) => {
  const primaryMain = getThemeColor(theme, MUIThemeColors.primaryMain)
  const primaryLight = getThemeColor(theme, MUIThemeColors.primaryLight)
  const primaryDark = getThemeColor(theme, MUIThemeColors.primaryDark)
  const white = getThemeColor(theme, MUIThemeColors.white)
  const backgroundMain = getThemeColor(theme, MUIThemeColors.backgroundMain)
  const secondaryLight = getThemeColor(theme, MUIThemeColors.secondaryLight)
  const secondaryMain = getThemeColor(theme, MUIThemeColors.secondaryMain)
  const infoLight = getThemeColor(theme, MUIThemeColors.infoLight)

  return {
    height: '750px',
    backgroundColor: white,
    fontFamily: theme.typography.fontFamily,
    position: 'relative',
    overflow: 'visible',

    // Toolbar styling
    '& .rbc-toolbar': {
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: `2px solid ${secondaryLight}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px',

      '& button': {
        color: primaryMain,
        border: `1px solid ${secondaryLight}`,
        backgroundColor: white,
        padding: '8px 16px',
        borderRadius: '6px',
        fontWeight: 600,
        fontSize: '14px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        minWidth: '80px',

        '&:hover': {
          borderColor: primaryMain,
          color: primaryDark,
          transform: 'translateY(-1px)',
          boxShadow: `0 1px 2px ${primaryLight}`,
        },

        '&:active': {
          transform: 'translateY(0)',
        },
      },

      '& .rbc-toolbar-label': {
        fontSize: '18px',
        fontWeight: 600,
        color: primaryDark,
        padding: '0 16px',
      },
    },

    // Header cells (day names)
    '& .rbc-header': {
      padding: '12px 8px',
      textAlign: 'center',
      fontSize: '13px',
      fontWeight: 600,
      color: primaryDark,
      backgroundColor: backgroundMain,
      borderBottom: `2px solid ${secondaryLight}`,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },

    // Calendar cells
    '& .rbc-day-bg': {
      borderRight: `1px solid ${secondaryLight}`,
      borderBottom: `1px solid ${secondaryLight}`,
      transition: 'background-color 0.2s ease',

      '&:hover': {
        backgroundColor: `${infoLight}40`,
      },
    },

    // Today cell
    '& .rbc-today': {
      backgroundColor: `${infoLight}50`,
      fontWeight: 600,
    },

    // Off-range cells (previous/next month)
    '& .rbc-off-range-bg': {
      backgroundColor: `${secondaryLight}B3`,
    },

    // Off-range date numbers
    '& .rbc-off-range': {
      color: `${secondaryMain}80`,
    },

    // Date numbers
    '& .rbc-date-cell': {
      padding: '4px 8px',
      textAlign: 'right',
      fontSize: '14px',
      color: secondaryMain,
      position: 'relative',
      zIndex: 1,
      flexShrink: 0,

      '& > a': {
        color: secondaryMain,
        textDecoration: 'none',
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',

        '&:hover': {
          backgroundColor: primaryLight,
          color: white,
        },
      },
    },

    // Month view cell content wrapper
    '& .rbc-day-bg + .rbc-events-container': {
      position: 'relative',
      zIndex: 2,
    },

    // Ensure proper overflow for month cells
    '& .rbc-row-content': {
      position: 'relative',
      overflow: 'visible',
      minHeight: '100px',
    },

    '& .rbc-row-segment': {
      padding: '2px',
      overflow: 'visible',
    },

    '& .rbc-current-time-indicator': {
      backgroundColor: primaryMain,
      height: '2px',
    },

    // Events with bar indicators at start and end
    '& .rbc-event': {
      backgroundColor: `${infoLight}90`,
      border: `1px solid ${infoLight}`,
      borderRadius: '4px',
      padding: '3px 6px',
      fontSize: '11px',
      fontWeight: 500,
      color: primaryDark,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      marginBottom: '2px',
      minHeight: '18px',
      lineHeight: '1.3',
      display: 'block',
      width: 'calc(100% - 4px)',
      boxSizing: 'border-box',
      position: 'relative',

      // Start bar (left side)
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '3px',
        backgroundColor: primaryMain,
        borderRadius: '4px 0 0 4px',
      },

      // End bar (right side)
      '&::after': {
        content: '""',
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: '3px',
        backgroundColor: primaryMain,
        borderRadius: '0 4px 4px 0',
      },

      '&:hover': {
        backgroundColor: `${infoLight}`,
        transform: 'scale(1.01)',
        zIndex: 10,
        position: 'relative',

        '&::before, &::after': {
          backgroundColor: primaryDark,
        },
      },

      '&:focus': {
        outline: `2px solid ${primaryLight}`,
        outlineOffset: '1px',
      },
    },

    // Hide start bar when event continues from previous segment
    '& .rbc-event.rbc-event-continues-prior::before': {
      display: 'none',
    },

    // Hide end bar when event continues into next segment
    '& .rbc-event.rbc-event-continues-after::after': {
      display: 'none',
    },

    // Adjust border radius when bars are hidden
    '& .rbc-event.rbc-event-continues-prior': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },

    '& .rbc-event.rbc-event-continues-after': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },

    '& .rbc-event-label': {
      fontSize: '11px',
      opacity: 0.9,
    },

    // Selected event
    '& .rbc-selected': {
      borderColor: primaryDark,

      '&::before, &::after': {
        backgroundColor: primaryDark,
        width: '4px',
      },
    },

    // Selected event styling
    '& .rbc-event-selected': {
      backgroundColor: `${primaryMain}`,
      color: `${white} !important`,
      borderColor: `${primaryDark} `,
      fontWeight: 600,

      '& .event-title, & .event-locality, & .event-separator': {
        color: `${white} `,
      },

      '&::before, &::after': {
        backgroundColor: `${primaryDark} `,
        width: '4px',
      },
    },

    // Month view specific
    '& .rbc-month-view': {
      border: `1px solid ${secondaryLight}`,
      borderRadius: '8px',
      overflow: 'visible',
      position: 'relative',
    },

    '& .rbc-month-row': {
      borderBottom: `1px solid ${secondaryLight}`,
      minHeight: '100px',
      position: 'relative',
    },

    // Day slot container - ensures proper spacing for events
    '& .rbc-day-slot': {
      minHeight: '90px',
      position: 'relative',
      paddingBottom: '4px',
    },

    // Events container in month view
    '& .rbc-events-container': {
      marginTop: '4px',
      marginBottom: '4px',
      paddingRight: '4px',
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      minHeight: '20px',
      overflow: 'visible',
    },

    // Month view events container - limit to 1 visible event
    '& .rbc-month-view .rbc-events-container': {
      maxHeight: 'none',
      overflow: 'visible',
    },

    // Hide all events except the first one in month view
    '& .rbc-month-view .rbc-day-slot .rbc-events-container .rbc-event:nth-child(n+2)': {
      display: 'none',
      visibility: 'hidden',
      height: 0,
      margin: 0,
      padding: 0,
    },

    // Ensure show more button is always visible and properly styled in month view
    '& .rbc-month-view .rbc-day-slot .rbc-events-container .rbc-show-more': {
      display: 'block',
      marginTop: '2px',
      visibility: 'visible',
    },

    // Event wrapper
    '& .rbc-event-content': {
      width: '100%',
      overflow: 'visible',
    },

    // Day view specific
    '& .rbc-time-view': {
      border: `1px solid ${secondaryLight}`,
      borderRadius: '8px',
      overflow: 'hidden',
    },

    '& .rbc-time-header': {
      borderBottom: `2px solid ${secondaryLight}`,
    },

    '& .rbc-time-content': {
      borderTop: `1px solid ${secondaryLight}`,
    },

    '& .rbc-time-slot': {
      borderTop: `1px solid ${secondaryLight}80`,
    },

    '& .rbc-time-header-content': {
      borderLeft: `1px solid ${secondaryLight}`,
    },

    // Agenda view
    '& .rbc-agenda-view': {
      '& table': {
        border: `1px solid ${secondaryLight}`,
        borderRadius: '8px',
        overflow: 'hidden',
      },

      '& .rbc-agenda-date-cell': {
        padding: '12px',
        backgroundColor: backgroundMain,
        fontWeight: 600,
        color: primaryDark,
      },

      '& .rbc-agenda-time-cell': {
        padding: '12px',
        color: secondaryMain,
      },

      '& .rbc-agenda-event-cell': {
        padding: '12px',
      },
    },

    // Show more link
    '& .rbc-show-more': {
      backgroundColor: infoLight,
      color: primaryMain,
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 600,
      marginTop: '4px',
      marginBottom: '4px',
      display: 'block',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      width: 'calc(100% - 4px)',
      boxSizing: 'border-box',
      textAlign: 'center',
      position: 'relative',
      zIndex: 5,
      overflow: 'visible',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',

      '&:hover': {
        backgroundColor: primaryLight,
        color: white,
        transform: 'scale(1.02)',
      },
    },
  }
})
